/**
 * Plugin to handle updating state label element, and state select element options, based on country select element changes. On page load, the select
 * list starts off containing all options for all countries. On page load and country change event, the relavent states are displayed while the others
 * are hidden. Due to lack of support for styling select option elements in Safari and Opera, options are converted into hidden divs and back and forth.
 *
 * @param     object     options     An object containing properties used for initialization.
 *                                   countrySelect          - A country select element wrapped in a jQuery object - REQUIRED
 *                                   stateSelect            - A state select element wrapped in a jQuery object - REQUIRED
 *                                   stateInput             - A state input element wrapped in a jQuery object - REQUIRED
 *                                   stateLabel             - A state label/span/div element wrapped in a jQuery object - OPTIONAL
 *                                   stateLabelDefaultValue - String value to override default state label value. Default is "Region".
 *
 * @return    object                 $this object.
 *
 * -----------------------------------------------------------------------------------------------------------------------------
 *
 * Special HTML properties:
 *
 *   csu-country-select-element - Country select element identifier.
 *   csu-state-label-element    - State label element identifier. (OPTIONAL)
 *   csu-state-label-colorize   - Whether or not to update label value color based on state required for country.
 *   csu-state-select-element   - State select element identifier.
 *   csu-state-label-value      - State select option property (1 for each country with states) - Label value to use for each country, State/Province/Region.
 *   csu-add-colon              - Add colon character to end of label if added as a class name to stateLabel. (Springboard uses colon chars for labels)
 *   csu-state-required         - State select option property (1 for each country with states) - Whether or not a state value is required for a country.
 *   csu-state-input-element    - State region text input element identifier.
 *   csu-index                  - This property must exist for each HTML element, country select, state select, state label and state input (region).
 *                                It's value links elements together. For example, csu-index="0" links to other elements with the same csu-index value
 *                                making it possible for multiple country/state blocks to exist on the same webpage.
 *
 * -----------------------------------------------------------------------------------------------------------------------------
 *
 * Example Usage:
 *
 *   1. COUNTRY SELECT ELEMENT
 *     <select id="country" name="country" class="csu-country-select-element" csu-index="0">
 *       <option value="US">United States</option>
 *       ...
 *     </select>
 *
 *   2. STATE LABEL ELEMENT
 *     <label for="state" class="csu-state-label-element csu-add-colon" csu-index="0" csu-state-label-colorize="true">State:</label>
 *
 *   3. STATE SELECT ELEMENT
 *     // Hidden to start otherwise element flashes can happen on page load.
 *     <select id="state" name="state" class="csu-state-select-element" csu-index="0" style="display:none;">
 *       <option
 *         value="NE"
 *         csu-option-country="US"
 *         csu-state-label-value="State"
 *         csu-state-required="true"
 *       >Nebraska</option>
 *       ...
 *     </select>
 *
 *   4. STATE/REGION INPUT ELEMENT
 *     // Hidden to start otherwise element flashes can happen on page load.
 *     <input type="text" id="region" name="region" value="" class="csu-state-input-element" csu-index="0" style="display:none;"/>
 *
 *   5. INCLUDE THIS FILE
 *     After HTML elements are setup then simply include this JS file.
 */
function countryStateUpdater (options) {
  const obj = this;

  /**
  * Initialize updater.
  *
  * @return    object    this object.
  */
  obj.initialize = (options) => {
    obj.$countrySelect         = options.countrySelect;
    obj.$stateSelect           = options.stateSelect;
    obj.$stateInput            = options.stateInput;
    obj.$stateLabel            = obj.validElement(options.stateLabel) ? options.stateLabel : null;
    obj.stateLabelDefaultValue = options.stateLabelDefaultValue ? options.stateLabelDefaultValue : 'Region';
    // Bind change action.
    obj.$countrySelect.bind('change', (event, dontResetValue) => {
      obj.onCountryChange(dontResetValue ? dontResetValue : false);
    });
    obj.onCountryChange(true);
    return obj;
  };

  /**
  * Country select element change event.
  *
  * @param     bool     dontResetValue     Whether or not to reset value on change event.
  * @return    none
  */
  obj.onCountryChange = (dontResetValue) => {
    // Grab current state select list value for later restoration.
    const currentStateSelectValue = obj.$stateSelect.find('option:selected').val();
    // Convert all state select options into hidden div elements (thanks Safari...) each time country select value changes.
    obj.$stateSelect.find('option').each(function () {
      const $this = $(this);
      const value = $this.val();
      // Ignore any value="" options.
      if (value && typeof value !== 'undefined') {
        $this.replaceWith(`<div
          value="${value}"
          csu-option-country="${$this.attr('csu-option-country')}"
          csu-state-label-value="${$this.attr('csu-state-label-value')}"
          style="display:none;"
        >${$this.text()}</div>`);
      }
    });
    // Grab country value.
    const country  = obj.$countrySelect.find('option:selected').val();
    // Grab state select div elements for country value.
    const $options = obj.$stateSelect.find(`div[csu-option-country="${country}"]`);
    if ($options.length) {
      // Show state select options for country by converting hidden div elements to option elements (thanks Safari...).
      $options.each(function () {
        const $this = $(this);
        $this.replaceWith(`<option
          value="${$this.attr('value')}"
          csu-option-country="${$this.attr('csu-option-country')}"
          csu-state-label-value="${$this.attr('csu-state-label-value')}"
        >${$this.text()}</option>`);
      });
      // Hide state input element.
      obj.$stateInput.hide();
      // Show state select element.
      obj.$stateSelect.show();
      // dontResetValue is set to true on page load so that any existing selection remains.
      obj.$stateSelect.val(dontResetValue ? currentStateSelectValue : '');
      // If a state label element was provided.
      if (obj.$stateLabel) {
        // If a custom label exists via the csu-state-label-value property.
        const $option = $options.eq(0);
        if ($option && $option.length && $option.attr('csu-state-label-value')) {
          // Update state label element value with custom value.
          obj.$stateLabel.html(`${$option.attr('csu-state-label-value')}${obj.$stateLabel.hasClass('csu-add-colon') ? ':' : ''}`);
        } else {
          // Update state label element value with default value.
          obj.$stateLabel.html(obj.stateLabelDefaultValue);
        }
        // If colorize label flag set. Only used in Springboard.
        if (obj.$stateLabel.attr('csu-state-label-colorize') === 'true') {
            // Make state label red if country requires a state value.
            // Also, if parent of obj.$stateLabel is a label then make it red too.
            if ($option.attr('csu-state-required') === 'true') {
              obj.$stateLabel.addClass('requiredFormField').addClass('red').parent('label').addClass('requiredFormField').addClass('red');
            } else {
              obj.$stateLabel.removeClass('requiredFormField').removeClass('red').parent('label').removeClass('requiredFormField').removeClass('red');
            }
        }
      }
    } else {
      // Hide state select element.
      obj.$stateSelect.hide();
      // Show state input element.
      obj.$stateInput.show();
      // dontResetValue is set to true on page load so existing value remains.
      if (!dontResetValue) {
        obj.$stateInput.val('');
      }
      // If a state label element was provided.
      if (obj.$stateLabel) {
        // Update state label element value with default value.
        obj.$stateLabel.html(`${obj.stateLabelDefaultValue}${obj.$stateLabel.hasClass('csu-add-colon') ? ':' : ''}`);
        // If colorize label flag set. Only used in Springboard.
        if (obj.$stateLabel.attr('csu-state-label-colorize') === 'true') {
          // Turn off red required state label for countries without states.
          // Also, if parent of obj.$stateLabel is a label then turn off it's red too.
          obj.$stateLabel.removeClass('requiredFormField').removeClass('red').parent('label').removeClass('requiredFormField').removeClass('red');
        }
      }
    }
  };

  /**
  * Validate options.
  *
  * @return    bool
  */
  obj.validateOptions = (options) => {
    // Options.
    if (typeof options !== 'object') {
      console.log('Country State Updater - Options missing or invalid.');
      return false;
    }
    // Country select element.
    if (!obj.validElement(options.countrySelect)){
      console.log('Country State Updater - Country selection field missing or invalid.');
      return false;
    }
    // State select element.
    if (!obj.validElement(options.stateSelect)){
      console.log('Country State Updater - State selection field missing or invalid.');
      return false;
    }
    // State input element.
    if (!obj.validElement(options.stateInput)){
      console.log('Country State Updater - State input field missing or invalid.');
      return false;
    }
    return true;
  };

  /**
  * Check if an HTML element is a valid jQuery object.
  *
  * @return    bool
  */
  obj.validElement = ($element) => {
    return typeof $element !== 'undefined' && $element instanceof jQuery && $element.length;
  };

  // Validate options.
  if (!obj.validateOptions(options)) {
    return null;
  }

  // initialize.
  return obj.initialize(options);
}

/**
 * This grabs HTML elements with valid csu- class names and valid csu-index values and initializes the
 * updater to handle country change events for them. Multiple country/state pairs per webpage is supported.
 */
$(document).ready(() => {
  // Each country select element.
  $('select.csu-country-select-element').not('.csu-initialized').each(function () {
    const $this  = $(this);
    // Grab associated state select element, based on matching csu-index values.
    $stateSelect = $(`select.csu-state-select-element[csu-index=${$this.attr('csu-index')}]`);
    // State select element must exist.
    if ($stateSelect.length) {
      // Initialize updater.
      new countryStateUpdater({
        countrySelect : $this,
        stateSelect   : $stateSelect,
        stateInput    : $(`input.csu-state-input-element[csu-index=${$this.attr('csu-index')}]`),
        stateLabel    : $(`.csu-state-label-element[csu-index=${$this.attr('csu-index')}]`)
      });
      $this.addClass('csu-initialized');
    }
  });
});
